import { THEMES, type Theme } from './palette'

/**
 * Ombres de la plateforme : UN identifiant choisi par l'exploitant dans une
 * liste fermée, traduit ici en valeurs des jetons `--shadow-*` de
 * `assets/main.css`, PAR THÈME — une ombre lisible sur fond clair disparaît sur
 * fond sombre, où l'élévation se lit surtout par un liseré clair.
 *
 * Fonctions PURES, comme `densite.ts` — l'application au document vit dans
 * `appliquer.ts`. Aucune valeur reçue n'entre dans la feuille : seul
 * l'identifiant voyage, les valeurs sont écrites dans ce fichier.
 */

/**
 * Modèles proposés. MIROIR de `STYLES_OMBRES` (backend
 * `core/apparence/schemas.ts`) et de la migration 0054 — un test backend fige
 * les trois.
 */
export const STYLES_OMBRES = ['plates', 'normales', 'marquees'] as const
export type StyleOmbres = (typeof STYLES_OMBRES)[number]

export interface OmbresPlateforme {
  style?: StyleOmbres
}

/** Les ombres de `main.css` : celles qu'on obtient sans aucune variable déclarée. */
export const DEFAUT_STYLE_OMBRES: StyleOmbres = 'normales'

/**
 * Paliers réglés. `--shadow-glow` n'y est pas : c'est un halo de la couleur
 * principale, déjà calculé par `palette.ts` — le régler ici ferait deux
 * feuilles en concurrence sur le même jeton.
 */
export const PALIERS_OMBRES = ['xs', 'sm', 'md', 'lg', 'xl'] as const
export type PalierOmbre = (typeof PALIERS_OMBRES)[number]

type JeuOmbres = Readonly<Record<PalierOmbre, string>>

const LISERE_CLAIR = '0 0 1px rgba(9, 30, 66, 0.31)'

/**
 * Valeurs de chaque modèle, par thème. « normales » recopie la couche ADS de
 * `main.css` (vérifié par test) : l'aperçu doit pouvoir la poser EN LIGNE, et
 * `var(--shadow-md)` est lu sans repli partout — `initial` y vaudrait « pas
 * d'ombre » au lieu du défaut.
 *
 * - plates   : plus d'ombre portée, le liseré seul. Il reste indispensable : sans
 *              lui, un menu déroulant se confond avec la page qu'il recouvre.
 *              Un peu plus marqué en sombre, où il porte seul l'élévation ;
 * - marquees : ombres plus longues et plus denses, même liseré.
 */
export const MODELES_OMBRES: Readonly<Record<StyleOmbres, Readonly<Record<Theme, JeuOmbres>>>> = {
  plates: {
    light: {
      xs: LISERE_CLAIR,
      sm: LISERE_CLAIR,
      md: LISERE_CLAIR,
      lg: LISERE_CLAIR,
      xl: LISERE_CLAIR,
    },
    dark: {
      xs: '0 0 0 1px rgba(188, 214, 240, 0.12)',
      sm: '0 0 0 1px rgba(188, 214, 240, 0.12)',
      md: '0 0 0 1px rgba(188, 214, 240, 0.12)',
      lg: '0 0 0 1px rgba(188, 214, 240, 0.12)',
      xl: '0 0 0 1px rgba(188, 214, 240, 0.12)',
    },
  },
  normales: {
    light: {
      xs: `0 1px 1px rgba(9, 30, 66, 0.25), ${LISERE_CLAIR}`,
      sm: `0 1px 1px rgba(9, 30, 66, 0.25), ${LISERE_CLAIR}`,
      md: `0 8px 12px rgba(9, 30, 66, 0.15), ${LISERE_CLAIR}`,
      lg: `0 8px 12px rgba(9, 30, 66, 0.15), ${LISERE_CLAIR}`,
      xl: `0 12px 24px rgba(9, 30, 66, 0.2), ${LISERE_CLAIR}`,
    },
    dark: {
      xs: '0 0 0 1px rgba(188, 214, 240, 0.04), 0 1px 1px rgba(3, 4, 4, 0.5)',
      sm: '0 0 0 1px rgba(188, 214, 240, 0.04), 0 1px 1px rgba(3, 4, 4, 0.5)',
      md: '0 0 0 1px rgba(188, 214, 240, 0.08), 0 8px 12px rgba(3, 4, 4, 0.36)',
      lg: '0 0 0 1px rgba(188, 214, 240, 0.08), 0 8px 12px rgba(3, 4, 4, 0.36)',
      xl: '0 0 0 1px rgba(188, 214, 240, 0.08), 0 12px 24px rgba(3, 4, 4, 0.5)',
    },
  },
  marquees: {
    light: {
      xs: `0 1px 2px rgba(9, 30, 66, 0.3), ${LISERE_CLAIR}`,
      sm: `0 2px 4px rgba(9, 30, 66, 0.28), ${LISERE_CLAIR}`,
      md: `0 12px 20px rgba(9, 30, 66, 0.22), ${LISERE_CLAIR}`,
      lg: `0 16px 28px rgba(9, 30, 66, 0.24), ${LISERE_CLAIR}`,
      xl: `0 24px 40px rgba(9, 30, 66, 0.28), ${LISERE_CLAIR}`,
    },
    dark: {
      xs: '0 0 0 1px rgba(188, 214, 240, 0.06), 0 1px 2px rgba(3, 4, 4, 0.6)',
      sm: '0 0 0 1px rgba(188, 214, 240, 0.06), 0 2px 4px rgba(3, 4, 4, 0.6)',
      md: '0 0 0 1px rgba(188, 214, 240, 0.1), 0 12px 20px rgba(3, 4, 4, 0.5)',
      lg: '0 0 0 1px rgba(188, 214, 240, 0.1), 0 16px 28px rgba(3, 4, 4, 0.55)',
      xl: '0 0 0 1px rgba(188, 214, 240, 0.1), 0 24px 40px rgba(3, 4, 4, 0.6)',
    },
  },
}

export function estStyleOmbres(valeur: unknown): valeur is StyleOmbres {
  return typeof valeur === 'string' && (STYLES_OMBRES as readonly string[]).includes(valeur)
}

/** Valide une forme reçue et la recopie, ou `null` si UN élément est hors contrat. */
export function normaliserOmbres(brut: unknown): OmbresPlateforme | null {
  if (typeof brut !== 'object' || brut === null || Array.isArray(brut)) return null
  const resultat: OmbresPlateforme = {}
  for (const [cle, valeur] of Object.entries(brut)) {
    if (cle !== 'style' || !estStyleOmbres(valeur)) return null
    resultat.style = valeur
  }
  return resultat
}

function verifier(style: StyleOmbres): void {
  if (!estStyleOmbres(style)) throw new Error(`Modèle d'ombres inconnu : ${JSON.stringify(style)}`)
}

/**
 * Tous les jetons d'un modèle pour un thème, valeurs EXPLICITES — y compris
 * pour « normales ». Pour l'aperçu, posé en ligne : il doit montrer le modèle du
 * brouillon même quand le document en applique un autre.
 */
export function jetonsOmbres(style: StyleOmbres, theme: Theme): Record<string, string> {
  verifier(style)
  const jeu = MODELES_OMBRES[style][theme]
  return Object.fromEntries(PALIERS_OMBRES.map((palier) => [`--shadow-${palier}`, jeu[palier]]))
}

/**
 * Déclarés sur la racine, comme dans `main.css`. Spécificité (0,2,1) : au-dessus
 * de `:root` et de `[data-theme='dark']` (0,1,0), quel que soit l'ordre
 * d'insertion des feuilles. Le contraste renforcé n'est PAS exclu,
 * contrairement aux couleurs : il ne redéfinit aucune ombre, et le liseré que
 * gardent tous les modèles sépare toujours les surfaces.
 */
export const SELECTEURS_OMBRES: Readonly<Record<Theme, string>> = {
  light: "html:root:not([data-theme='dark'])",
  dark: "html:root[data-theme='dark']",
}

/** Feuille du modèle, `''` pour le modèle par défaut. Lève sur un modèle inconnu. */
export function feuilleOmbres(ombres: OmbresPlateforme): string {
  const style = ombres.style
  if (style === undefined) return ''
  verifier(style)
  if (style === DEFAUT_STYLE_OMBRES) return ''
  return THEMES.map((theme) => {
    const lignes = Object.entries(jetonsOmbres(style, theme)).map(([n, v]) => `  ${n}: ${v};`)
    return `${SELECTEURS_OMBRES[theme]} {\n${lignes.join('\n')}\n}`
  }).join('\n')
}
